import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, fileUrl } from "@/lib/api";
import { useCart } from "@/lib/cart";
import { usePublicSettings } from "@/lib/settings";
import { Loader2, Trash2, ShoppingCart, ArrowLeft, ImageOff, Truck, Package } from "lucide-react";
import { toast } from "sonner";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const Cart = () => {
  const cart = useCart();
  const settings = usePublicSettings();
  const navigate = useNavigate();
  const [fulfilment, setFulfilment] = useState("pickup");
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", notes: "" });
  const [creating, setCreating] = useState(false);

  const shippingTotal = fulfilment === "shipping" ? cart.shippingTotal : 0;
  const grandTotal = cart.subtotal + shippingTotal;

  const checkout = async () => {
    if (cart.items.length === 0) return;
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    if (fulfilment === "shipping" && !form.address.trim()) {
      toast.error("Shipping address required for delivery");
      return;
    }
    setCreating(true);
    try {
      const res = await api.post("/checkout/session", {
        items: cart.items.map((i) => ({ product_id: i.id, quantity: i.quantity })),
        fulfilment,
        customer_name: form.name.trim(),
        customer_email: form.email.trim(),
        customer_phone: form.phone.trim() || null,
        shipping_address: fulfilment === "shipping" ? form.address.trim() : null,
        notes: form.notes.trim() || null,
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error("Stripe didn't return a URL");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout");
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-spark-floor">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        <Link to="/shop" className="inline-flex items-center gap-1 text-sm text-spark-slate hover:text-spark-teal mb-4">
          <ArrowLeft className="size-4" /> Continue shopping
        </Link>
        <h1 className="font-display uppercase tracking-tight text-3xl sm:text-4xl font-bold text-spark-ink mb-6">Your cart</h1>

        {cart.items.length === 0 ? (
          <div className="surface-card p-12 text-center">
            <ShoppingCart className="size-10 text-spark-line mx-auto mb-3" />
            <p className="font-display uppercase tracking-wider font-bold text-spark-ink">Cart is empty</p>
            <p className="text-sm text-spark-slate mt-1">Browse the shop to add parts or merch.</p>
            <Link to="/shop" className="btn-primary mt-5">Go to shop</Link>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-3">
              {cart.items.map((it) => (
                <div key={it.id} className="surface-card p-4 flex items-center gap-4" data-testid={`cart-item-${it.sku}`}>
                  <div className="size-20 rounded-md overflow-hidden bg-spark-floor border border-spark-line shrink-0">
                    {it.image ? <img src={fileUrl(it.image)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-spark-line"><ImageOff className="size-6" /></div>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-display uppercase tracking-tight font-bold text-spark-ink line-clamp-1">{it.name}</p>
                    <p className="text-xs font-mono text-spark-slate">{it.sku}</p>
                    <p className="font-display uppercase tracking-tight text-lg font-bold text-spark-ink mt-1">{money(it.price)}</p>
                  </div>
                  <div className="flex items-center border border-spark-line rounded-md overflow-hidden">
                    <button onClick={() => cart.setQty(it.id, it.quantity - 1)} className="px-2.5 py-1 hover:bg-spark-floor">−</button>
                    <span className="px-3 py-1 font-bold tabular-nums">{it.quantity}</span>
                    <button onClick={() => cart.setQty(it.id, it.quantity + 1)} className="px-2.5 py-1 hover:bg-spark-floor">+</button>
                  </div>
                  <button onClick={() => cart.remove(it.id)} className="p-2 text-spark-slate hover:text-red-500" data-testid={`cart-remove-${it.sku}`} title="Remove"><Trash2 className="size-4" /></button>
                </div>
              ))}

              <div className="surface-card p-5">
                <p className="label-mono mb-3">Fulfilment</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <button
                    onClick={() => setFulfilment("pickup")}
                    className={`p-4 rounded-md border-2 text-left transition ${fulfilment === "pickup" ? "border-spark-teal bg-spark-teal-light/40" : "border-spark-line hover:border-spark-teal/60"}`}
                    data-testid="fulfilment-pickup"
                  >
                    <Package className="size-5 text-spark-teal mb-2" />
                    <p className="font-display uppercase tracking-tight font-bold text-spark-ink">Free pickup</p>
                    <p className="text-xs text-spark-slate mt-0.5">{settings.address_line || "Workshop pickup"}{settings.city ? ` · ${settings.city}` : ""}</p>
                  </button>
                  <button
                    onClick={() => setFulfilment("shipping")}
                    className={`p-4 rounded-md border-2 text-left transition ${fulfilment === "shipping" ? "border-spark-teal bg-spark-teal-light/40" : "border-spark-line hover:border-spark-teal/60"}`}
                    data-testid="fulfilment-shipping"
                  >
                    <Truck className="size-5 text-spark-teal mb-2" />
                    <p className="font-display uppercase tracking-tight font-bold text-spark-ink">NZ Shipping · {money(cart.shippingTotal)}</p>
                    <p className="text-xs text-spark-slate mt-0.5">Tracked courier, 2–4 working days</p>
                  </button>
                </div>
              </div>

              <div className="surface-card p-5 space-y-4">
                <p className="label-mono">Your details</p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <input className="input-style" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="checkout-name" />
                  <input className="input-style" type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="checkout-email" />
                  <input className="input-style sm:col-span-2" placeholder="Phone (optional)" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="checkout-phone" />
                  {fulfilment === "shipping" && (
                    <textarea className="input-style sm:col-span-2 resize-none" rows={2} placeholder="Shipping address (street, suburb, city, postcode)" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="checkout-address" />
                  )}
                  <textarea className="input-style sm:col-span-2 resize-none" rows={2} placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="checkout-notes" />
                </div>
              </div>
            </div>

            <aside className="lg:sticky lg:top-24 self-start">
              <div className="surface-card p-5 space-y-3">
                <p className="label-mono">Order summary</p>
                <ul className="text-sm divide-y divide-spark-line">
                  <li className="flex justify-between py-2"><span>Subtotal</span><span className="font-bold tabular-nums">{money(cart.subtotal)}</span></li>
                  <li className="flex justify-between py-2"><span>Shipping</span><span className="font-bold tabular-nums">{money(shippingTotal)}</span></li>
                  <li className="flex justify-between py-3 bg-spark-floor -mx-5 px-5">
                    <span className="font-display uppercase tracking-wider font-bold">Total</span>
                    <span className="font-display uppercase tracking-tight text-2xl font-bold tabular-nums" data-testid="cart-grand-total">{money(grandTotal)}</span>
                  </li>
                </ul>
                <button
                  onClick={checkout}
                  disabled={creating}
                  className="btn-accent w-full"
                  data-testid="checkout-btn"
                >
                  {creating ? <Loader2 className="size-4 animate-spin" /> : <ShoppingCart className="size-4" />}
                  {creating ? "Redirecting…" : "Pay with Stripe"}
                </button>
                <p className="text-[10px] text-spark-slate text-center">Secure checkout · Apple Pay, Google Pay, and cards accepted.</p>
              </div>
            </aside>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
};

export default Cart;
